import { createApi } from '@reduxjs/toolkit/query/react';
import { ChildCollectionEnum } from 'core';

import { axiosBaseQuery } from '../axiosBaseQuery';

import { CancellationInfo, CandidateSummary, ChildDetails, FamilyAvailableSessions } from './types';
import {
  CancellationInfoResponse,
  CandidateSummaryResponse,
  ChildDetailsResponse,
  FamilyAvailableSessionsResponse,
} from './apiTypes';
import { mapCandidateSummaryResponse, mapChildDetailsResponse } from './myRegistrationsHelpers';

export const myRegistrationsApi = createApi({
  reducerPath: 'myRegistrationsApi',
  baseQuery: axiosBaseQuery({
    baseUrl: '/web-api',
  }),
  endpoints: (builder) => ({
    getCandidateSummary: builder.query<CandidateSummary, string>({
      query: (userName) => ({
        url: '/api/examregistation/b2c/candidates/details',
        method: 'post',
        data: { UserName: userName },
      }),
      transformResponse: (data: CandidateSummaryResponse) => mapCandidateSummaryResponse(data),
    }),
    getCandidateFamilySessions: builder.query<FamilyAvailableSessions[], number>({
      query: (candidateId) => ({
        url: `/b2c/api/reports/familyAvailableSessions?CandidateId=${candidateId}`,
        method: 'get',
      }),
      transformResponse: (data: FamilyAvailableSessionsResponse[]) =>
        data.map((x) => ({
          candidateId: x.CandidateId,
          sessions: x.SessionAwardingBody.map((y) => ({ id: y.SessionId, name: y.Name })),
        })),
    }),
    getRegistrationsReport: builder.query<
      { file: Blob; headers: { [key: string]: string } },
      { candidateId: number; countryId: number; sessionId: number }
    >({
      query: ({ candidateId, countryId, sessionId }) => ({
        url: `/b2c/api/reports/fullRegistrationSummaryReport?CandidateId=${candidateId}&CountryId=${countryId}&SessionId=${sessionId}`,
        method: 'get',
        config: { responseType: 'blob' },
      }),
      transformResponse: (file: Blob, meta: { headers: { [key: string]: string } }) => ({
        file,
        headers: meta?.headers,
      }),
    }),
    getOrganisationCountryConfigByDistrictId: builder.query<{ allowAmendments: boolean }, number>({
      query: (districtId) => ({
        url: `/api/country/getOrganisationCountryConfigByDistrictId/${districtId}`,
        method: 'get',
      }),
      transformResponse: ({ AllowAmendments }) => ({ allowAmendments: AllowAmendments }),
    }),
    getChildDetails: builder.query<ChildDetails, number>({
      query: (registrationGroupId) => ({
        url: `/api/registrationGroup/getChildDetails/${registrationGroupId}`,
        method: 'get',
      }),
      transformResponse: (data: ChildDetailsResponse) => mapChildDetailsResponse(data),
    }),
    updateChildDetails: builder.mutation<
      void,
      { childDetails: Omit<ChildDetails, 'dateOfBirth'>; registrationGroupId: number }
    >({
      query: ({ childDetails, registrationGroupId }) => ({
        url: `/api/registrationGroup/updateChildDetails/`,
        method: 'post',
        data: {
          updateChildDetails: [
            {
              registrationGroupId,
              newChildDetail: {
                ...childDetails,
                allowedPeople:
                  childDetails.childCollectionOption === ChildCollectionEnum.ParentPickup
                    ? childDetails.allowedPeople
                    : [],
              },
            },
          ],
        },
      }),
    }),
    getCancellationInfo: builder.query<CancellationInfo, number>({
      query: (registrationId) => ({
        url: `/api/examregistation/cancellationInfo/${registrationId}`,
        method: 'get',
      }),
      transformResponse: (data: CancellationInfoResponse) => ({
        cancellationCreator: data.CancellationCreator,
        cancellationDate: data.CancellationDate,
        cancellationNote: data.CancellationNote,
        cancellationReason: data.CancellationReason,
      }),
    }),
    getCancellationReasons: builder.query<{ id: string; name: string }[], void>({
      query: () => ({
        url: `/api/payment/cancelationReasons`,
        method: 'get',
      }),
      transformResponse: (data: { Id: number; Name: string }[]) => data.map((x) => ({ id: `${x.Id}`, name: x.Name })),
    }),
    cancelRegistration: builder.mutation<
      void,
      { registrationId: number; cancellationReasonId: number; cancellationInfo: string }
    >({
      query: (data) => ({
        url: `/api/payment/registration/cancel`,
        method: 'post',
        data: {
          RegistrationId: data.registrationId,
          CancelationInfo: data.cancellationInfo,
          CancelationReasonId: data.cancellationReasonId,
        },
      }),
    }),
  }),
});

export const {
  useLazyGetCandidateSummaryQuery,
  useLazyGetCandidateFamilySessionsQuery,
  useLazyGetRegistrationsReportQuery,
  useGetChildDetailsQuery,
  useUpdateChildDetailsMutation,
  useGetOrganisationCountryConfigByDistrictIdQuery,
  useLazyGetOrganisationCountryConfigByDistrictIdQuery,
  useLazyGetCancellationInfoQuery,
  useGetCancellationReasonsQuery,
  useCancelRegistrationMutation,
} = myRegistrationsApi;
